import { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  ModalProps,
  Stack,
  TextField,
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs from 'dayjs';

import { type CreateApplicationTokenDTO } from '../../../../api';
import type { InfoFieldChangeHandler } from '../../../../components';

type CreateAppTokenModalProps = {
  open: boolean;
  onClose: () => void;
  onCreate: () => void;
  fields: CreateApplicationTokenDTO['body'] | null;
  fieldsHandler: InfoFieldChangeHandler;
  isLoading: boolean;
};

export const CreateAppTokenModal: FC<CreateAppTokenModalProps> = (props) => {
  const { open, onClose, onCreate, fields, fieldsHandler, isLoading } = props;
  const { t } = useTranslation();

  // allowed IPs input logic
  const ipsRef = useRef<HTMLInputElement>(null);
  const [isFocused, setFocus] = useState(false);

  const addIpToArray = () => {
    if (!ipsRef.current?.value) return;

    let newIpsArray;
    if (!fields?.allowed_ips) {
      newIpsArray = [ipsRef.current.value];
    } else {
      newIpsArray = [...fields.allowed_ips, ipsRef.current.value];
    }
    fieldsHandler(newIpsArray, true, 'allowed_ips');
    ipsRef.current.value = '';
    setFocus(false);
  };

  const removeIpFromArray = (index: number) => {
    if (!fields?.allowed_ips) return;

    const newIpsArray = [...fields.allowed_ips];
    newIpsArray.splice(index, 1);
    fieldsHandler(newIpsArray, true, 'allowed_ips');
  };

  // validation for required fields
  const requiredFields = [
    'name',
    'expired_at',
    'allowed_ips',
  ] as (keyof CreateApplicationTokenDTO['body'])[];
  const checkSubmitDisabling = () => {
    if (!fields) return true;

    for (const field of requiredFields) {
      if (
        fields[field] === undefined ||
        fields[field] === '' ||
        fields[field] === null ||
        (Array.isArray(fields[field]) && fields[field].length === 0)
      ) {
        return true;
      }
    }

    return false;
  };

  // close handlers
  const closeHandlerWithPrevent: ModalProps['onClose'] = (_, reason) => {
    if (reason === 'backdropClick') return;
    onClose();
  };
  const closeHandler = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => closeHandlerWithPrevent(_, reason)}
      PaperProps={{ sx: { minWidth: '380px' } }}
    >
      <DialogTitle>{t('app-tokens.modals.create.title')}</DialogTitle>
      <IconButton
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
        onClick={() => closeHandler()}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent>
        <Stack direction="column" spacing={2}>
          <TextField
            name="name"
            value={fields?.name ?? ''}
            onChange={(e) => fieldsHandler(e)}
            label={t('app-tokens.modals.create.fields.name')}
            fullWidth
          />

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              name="expired_at"
              value={fields?.expired_at ? dayjs(fields.expired_at) : null}
              onChange={(newValue) => fieldsHandler(newValue, true, 'expired_at')}
              format="DD.MM.YYYY"
              disablePast
              label={t('app-tokens.modals.create.fields.expired-at')}
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'outlined',
                },
              }}
            />
          </LocalizationProvider>

          <TextField
            inputRef={ipsRef}
            name="allowed_ips"
            label={t('app-tokens.modals.create.fields.allowed-ips')}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton aria-label="add" sx={{ marginRight: '-11px' }} onClick={addIpToArray}>
                    <AddIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            // right focus logic with ref usage
            InputLabelProps={{ shrink: Boolean(ipsRef.current?.value) || isFocused }}
            onFocus={() => setFocus(true)}
            onBlur={() => setFocus(false)}
          />
          <Stack
            direction="row"
            flexWrap="wrap"
            gap={1}
            sx={{ width: '332px', marginTop: '6px !important' }}
          >
            {fields?.allowed_ips &&
              fields.allowed_ips.map((ip, index) => (
                <Chip key={index} label={ip} onDelete={() => removeIpFromArray(index)} />
              ))}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          onClick={() => onCreate()}
          disabled={isLoading || checkSubmitDisabling()}
        >
          {t('app-tokens.modals.create.create-btn')}
        </Button>
        <Button variant="outlined" onClick={() => onClose()}>
          {t('app-tokens.modals.cancel-btn')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
